import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Search, MapPin, Calendar, Zap, Crown, Truck, Flame, User, Wrench, Star, ArrowRight } from 'lucide-react';
import { getVehicles, getCategories } from '../api';

const Home = () => {
  const navigate = useNavigate();
  const [searchLocation, setSearchLocation] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const iconMap = {
    Zap,
    Crown,
    Truck,
    Flame,
    User,
    Wrench
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [vehiclesData, categoriesData] = await Promise.all([
          getVehicles({ limit: 6 }),
          getCategories()
        ]);
        setFeaturedVehicles(vehiclesData);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative h-[600px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1614200179396-2bdb77ebf81b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXJ8ZW58MHx8fHwxNzYxNjEzMDE2fDA&ixlib=rb-4.1.0&q=85"
            alt="Luxury Car"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        </div>
        
        <div className="relative h-full max-w-7xl mx-auto px-6 flex flex-col justify-center">
          <h1 className="text-6xl font-bold text-white mb-4 tracking-tight">
            Experience Luxury,<br />
            <span className="text-red-600">Your Way</span>
          </h1>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl">
            Premium vehicles, professional chauffeurs, and concierge services at your fingertips
          </p>
          
          {/* Search Bar */}
          <Card className="p-6 max-w-4xl bg-white/95 backdrop-blur border-0 shadow-2xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Where"
                  value={searchLocation}
                  onChange={(e) => setSearchLocation(e.target.value)}
                  className="pl-10 h-12 border-gray-200 focus:border-red-600 focus:ring-red-600"
                />
              </div>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="date"
                  placeholder="Check in"
                  className="pl-10 h-12 border-gray-200 focus:border-red-600 focus:ring-red-600"
                />
              </div>
              <Button 
                onClick={() => navigate('/search')}
                className="h-12 bg-red-600 hover:bg-red-700 text-white font-semibold transition-all hover:shadow-lg"
              >
                <Search className="mr-2 h-5 w-5" />
                Search
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Categories Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-black mb-8">Browse by Category</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {loading ? (
            <div className="col-span-full text-center py-8 text-gray-500">Loading categories...</div>
          ) : (
            categories.map((category) => {
              const Icon = iconMap[category.icon];
              return (
                <Card
                  key={category.id}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    navigate(`/search?category=${category.id}`);
                  }}
                  className="p-6 cursor-pointer hover:shadow-xl transition-all hover:-translate-y-1 border-gray-200 group"
                >
                  <div className="flex flex-col items-center text-center space-y-3">
                    <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center group-hover:bg-red-600 transition-colors">
                      <Icon className="h-6 w-6 text-red-600 group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-black">{category.name}</p>
                      <p className="text-xs text-gray-500 mt-1">{category.count} available</p>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      </div>

      {/* Featured Vehicles */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-3xl font-bold text-black">Featured Vehicles</h2>
              <p className="text-gray-600 mt-2">Handpicked luxury vehicles for your next adventure</p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => navigate('/search')}
              className="border-black text-black hover:bg-black hover:text-white transition-all"
            >
              View All
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              <div className="col-span-full text-center py-8 text-gray-500">Loading vehicles...</div>
            ) : (
              featuredVehicles.map((vehicle) => (
                <Card
                  key={vehicle.id}
                  onClick={() => navigate(`/vehicle/${vehicle.id}`)}
                  className="overflow-hidden cursor-pointer hover:shadow-2xl transition-all hover:-translate-y-2 border-0 group"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={vehicle.image}
                      alt={vehicle.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 bg-black/80 backdrop-blur px-3 py-1 rounded-full">
                      <span className="text-white font-semibold">${vehicle.price}/day</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-xl font-bold text-black group-hover:text-red-600 transition-colors">{vehicle.name}</h3>
                        <p className="text-sm text-gray-500">{vehicle.category}</p>
                      </div>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mb-3">
                      <MapPin className="h-4 w-4 mr-1" />
                      <span>{vehicle.location}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 mr-1" />
                        <span className="font-semibold text-black">{vehicle.rating}</span>
                        <span className="text-gray-500 ml-1">({vehicle.reviewCount})</span>
                      </div>
                      <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white">
                        Book Now
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-black text-center mb-12">Why Choose YourAutoConcierge</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="h-16 w-16 mx-auto rounded-full bg-red-600 flex items-center justify-center mb-4">
              <Crown className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-black mb-2">Premium Fleet</h3>
            <p className="text-gray-600">Access to the finest luxury and exotic vehicles, meticulously maintained to perfection.</p>
          </div>
          <div className="text-center">
            <div className="h-16 w-16 mx-auto rounded-full bg-red-600 flex items-center justify-center mb-4">
              <User className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-black mb-2">Professional Service</h3>
            <p className="text-gray-600">Experienced chauffeurs and concierge staff dedicated to your satisfaction.</p>
          </div>
          <div className="text-center">
            <div className="h-16 w-16 mx-auto rounded-full bg-red-600 flex items-center justify-center mb-4">
              <Zap className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-black mb-2">Instant Booking</h3>
            <p className="text-gray-600">Real-time availability and instant confirmation for your convenience.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
